import { useEffect } from "react";

/* SEO Head — manages <title>, <meta description>, canonical, OG tags, and
 * JSON-LD Article schema for any standalone SEO article page. Google's
 * modern Googlebot executes JS and picks these up, so for a CRA SPA this
 * is enough without server-side rendering. If we later add SSR (Next or
 * Vite SSR) the same data can be hoisted into the server response.
 *
 * Why no react-helmet: keeping dependencies lean. This effect runs once
 * per route change and cleans up nothing — the next route's SeoHead
 * overwrites the same tags.
 */
function upsertMeta(name, content, attr = "name") {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function upsertLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
    let el = document.head.querySelector(`script[data-seo="${id}"]`);
    if (!el) {
        el = document.createElement("script");
        el.setAttribute("type", "application/ld+json");
        el.setAttribute("data-seo", id);
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

export default function SeoHead({ title, description, canonicalPath, articleData, image, imageAlt }) {
    useEffect(() => {
        if (title) document.title = title;
        upsertMeta("description", description);
        upsertMeta("og:title", title, "property");
        upsertMeta("og:description", description, "property");
        upsertMeta("og:type", "article", "property");
        upsertMeta("twitter:card", "summary_large_image");
        upsertMeta("twitter:title", title);
        upsertMeta("twitter:description", description);
        if (image) {
            upsertMeta("og:image", image, "property");
            upsertMeta("og:image:secure_url", image, "property");
            upsertMeta("twitter:image", image);
            if (imageAlt) {
                upsertMeta("og:image:alt", imageAlt, "property");
                upsertMeta("twitter:image:alt", imageAlt);
            }
        }
        if (canonicalPath) {
            const origin = typeof window !== "undefined" ? window.location.origin : "";
            upsertLink("canonical", `${origin}${canonicalPath}`);
            upsertMeta("og:url", `${origin}${canonicalPath}`, "property");
        }
        if (articleData) {
            upsertJsonLd("article", articleData);
        }
        return () => {
            // Remove only our JSON-LD between routes so we don't pile up
            // stale Article schema. Title/meta get overwritten by the next
            // SeoHead anyway.
            const el = document.head.querySelector(`script[data-seo="article"]`);
            if (el) el.remove();
        };
    }, [title, description, canonicalPath, articleData]);
    return null;
}
